import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Search, Users, ListChecks, Activity, Settings2, BookOpen, LogOut, Menu, X, Gauge } from "lucide-react";
import { useAuth } from "../lib/auth";

const NAV = [
  { to: "/", label: "Find Restaurants", Icon: Search, end: true },
  { to: "/leads", label: "Saved Leads", Icon: ListChecks },
  { to: "/jobs", label: "Research Jobs", Icon: Activity },
];
const ADMIN_NAV = [
  { to: "/admin/users", label: "Users", Icon: Users },
  { to: "/admin/services", label: "Service Catalog", Icon: BookOpen },
  { to: "/admin/integrations", label: "Integrations & Limits", Icon: Settings2 },
  { to: "/admin/usage", label: "Research API Usage", Icon: Gauge },
];

const Item = ({ to, label, Icon, end, onClick }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className={({ isActive }) =>
      `flex items-center gap-3 border-l-4 px-4 py-2.5 text-sm font-medium transition-colors ${
        isActive ? "border-orange-500 bg-slate-800 text-white" : "border-transparent text-slate-300 hover:bg-slate-800/60 hover:text-white"
      }`
    }
  >
    <Icon className="h-4 w-4" /> {label}
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <div className="flex min-h-screen">
      <aside className={`no-print fixed inset-y-0 left-0 z-40 w-64 transform bg-slate-900 transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center justify-between px-5">
          <div data-testid="brand-wordmark" className="text-lg font-extrabold tracking-tight">
            <span className="text-orange-500">360RGE</span> <span className="text-slate-200">Lead Finder</span>
          </div>
          <button className="text-slate-300 lg:hidden" onClick={close} data-testid="sidebar-close-btn"><X className="h-5 w-5" /></button>
        </div>
        <nav className="mt-2 space-y-0.5">
          {NAV.map((n) => <Item key={n.to} {...n} onClick={close} />)}
          {user?.role === "admin" && (
            <>
              <div className="px-5 pb-1 pt-6 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Admin</div>
              {ADMIN_NAV.map((n) => <Item key={n.to} {...n} onClick={close} />)}
            </>
          )}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-slate-800 p-4">
          <div className="text-sm font-semibold text-white" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-slate-400">{user?.email} · {user?.role}</div>
          <button onClick={logout} data-testid="logout-btn" className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-orange-400">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" onClick={close} />}
      <div className="min-w-0 flex-1">
        <header className="no-print sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-white/90 px-4 backdrop-blur lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="sidebar-open-btn"><Menu className="h-5 w-5" /></button>
          <span className="font-extrabold"><span className="text-orange-600">360RGE</span> Lead Finder</span>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
